import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { setOnApiVersionChange } from 'src/api';
import { queryClient } from 'src/api/queries';

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface VersionCheckContextType {
  hasUpdate: boolean;
  currentVersion: string | null;
  latestVersion: string | null;
  reload: () => void;
}

const VersionCheckContext = createContext<VersionCheckContextType | null>(null);

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;
const STORAGE_KEY = 'app_version';
const API_VERSION_STORAGE_KEY = 'api_version';

// Disable version checking in development mode
const IS_DEV = import.meta.env.DEV;

// A static asset, not API data — deliberately outside the identity-scoped
// queryKeys tree.
const VERSION_QUERY_KEY = ['appVersion'] as const;

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    // Add cache-busting query param to ensure we get the latest version
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

export function VersionCheckProvider({ children }: { children: ReactNode }) {
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);

  // This provider sits above QueryClientProvider (src/index.tsx) so the
  // client is passed explicitly rather than read from context.
  const { data: latest } = useQuery(
    {
      queryKey: VERSION_QUERY_KEY,
      queryFn: fetchVersion,
      enabled: !IS_DEV,
      staleTime: 0,
      gcTime: 0,
      refetchInterval: VERSION_CHECK_INTERVAL,
      retry: false,
      meta: { silent: true },
    },
    queryClient,
  );

  const latestVersion = latest?.version ?? null;

  useEffect(() => {
    if (IS_DEV) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setCurrentVersion(stored);
      return;
    }
    // First load — whatever the poll sees first becomes the baseline.
    if (latestVersion) {
      localStorage.setItem(STORAGE_KEY, latestVersion);
      setCurrentVersion(latestVersion);
    }
  }, [latestVersion]);

  useEffect(() => {
    setOnApiVersionChange(() => setHasUpdate(true));
  }, []);

  useEffect(() => {
    if (!currentVersion || !latestVersion) return;
    if (latestVersion !== currentVersion) setHasUpdate(true);
  }, [currentVersion, latestVersion]);

  const reload = useCallback(() => {
    // Clear stored versions so new version becomes current on reload
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(API_VERSION_STORAGE_KEY);
    window.location.reload();
  }, []);

  return (
    <VersionCheckContext.Provider
      value={{
        hasUpdate,
        currentVersion,
        latestVersion,
        reload,
      }}
    >
      {children}
    </VersionCheckContext.Provider>
  );
}

export function useVersionCheck(): VersionCheckContextType {
  const context = useContext(VersionCheckContext);
  if (!context) {
    throw new Error(
      'useVersionCheck must be used within a VersionCheckProvider',
    );
  }
  return context;
}
